import React from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import './Breadcrumbs.css';

const ROUTE_CONFIG = [
  { path: '/', labelKey: 'breadcrumbs.dashboard', parent: null },
  { path: '/menu', labelKey: 'breadcrumbs.menu', parent: '/' },
  { path: '/menu/modifiers', labelKey: 'breadcrumbs.modifiers', parent: '/menu' },
  { path: '/menu/items/new', labelKey: 'breadcrumbs.newItem', parent: '/menu' },
  {
    path: '/menu/items/edit/:itemId',
    labelKey: 'breadcrumbs.editItem',
    parent: '/menu',
    dynamic: true,
  },
  { path: '/menu/combos/new', labelKey: 'breadcrumbs.newCombo', parent: '/menu' },
  {
    path: '/menu/combos/:itemId/edit',
    labelKey: 'breadcrumbs.editCombo',
    parent: '/menu',
    dynamic: true,
  },
  { path: '/orders', labelKey: 'breadcrumbs.orders', parent: '/' },
  { path: '/tables', labelKey: 'breadcrumbs.tables', parent: '/' },
  { path: '/delivery', labelKey: 'breadcrumbs.delivery', parent: '/' },
  {
    path: '/delivery/integrations',
    labelKey: 'breadcrumbs.deliveryIntegrations',
    parent: '/delivery',
  },
  { path: '/cash-register', labelKey: 'breadcrumbs.cashRegister', parent: '/' },
  { path: '/expenses', labelKey: 'breadcrumbs.expenses', parent: '/' },
  { path: '/invoicing', labelKey: 'breadcrumbs.invoicing', parent: '/' },
  { path: '/inventory', labelKey: 'breadcrumbs.inventory', parent: '/' },
  { path: '/inventory/movements', labelKey: 'breadcrumbs.movements', parent: '/inventory' },
  { path: '/inventory/setup', labelKey: 'breadcrumbs.inventorySetup', parent: '/inventory' },
  { path: '/inventory/count', labelKey: 'breadcrumbs.inventoryCount', parent: '/inventory' },
  {
    path: '/inventory/consolidated',
    labelKey: 'breadcrumbs.inventoryConsolidated',
    parent: '/inventory',
  },
  {
    path: '/inventory/transfers',
    labelKey: 'breadcrumbs.inventoryTransfers',
    parent: '/inventory',
  },
  { path: '/inventory/org-recipes', labelKey: 'breadcrumbs.orgRecipes', parent: '/inventory' },
  {
    path: '/inventory/suggestions',
    labelKey: 'breadcrumbs.inventorySuggestions',
    parent: '/inventory',
  },
  { path: '/inventory/reports', labelKey: 'breadcrumbs.inventoryReports', parent: '/inventory' },
  { path: '/inventory/suppliers', labelKey: 'breadcrumbs.suppliers', parent: '/inventory' },
  {
    path: '/inventory/suppliers/:supplierId',
    labelKey: 'breadcrumbs.supplierDetail',
    parent: '/inventory/suppliers',
    dynamic: true,
  },
  {
    path: '/inventory/purchase-orders',
    labelKey: 'breadcrumbs.purchaseOrders',
    parent: '/inventory',
  },
  {
    path: '/inventory/purchase-orders/new',
    labelKey: 'breadcrumbs.newPurchaseOrder',
    parent: '/inventory/purchase-orders',
  },
  {
    path: '/inventory/purchase-orders/:orderId',
    labelKey: 'breadcrumbs.purchaseOrderDetail',
    parent: '/inventory/purchase-orders',
    dynamic: true,
  },
  { path: '/customers', labelKey: 'breadcrumbs.customers', parent: '/' },
  {
    path: '/customers/:customerId',
    labelKey: 'breadcrumbs.customerDetail',
    parent: '/customers',
    dynamic: true,
  },
  { path: '/restaurant', labelKey: 'breadcrumbs.restaurants', parent: '/' },
  { path: '/restaurants', labelKey: 'breadcrumbs.restaurants', parent: '/' },
  { path: '/restaurant/new', labelKey: 'breadcrumbs.newRestaurant', parent: '/restaurant' },
  {
    path: '/restaurant/edit/:restaurantId',
    labelKey: 'breadcrumbs.editRestaurant',
    parent: '/restaurant',
    dynamic: true,
  },
  { path: '/users', labelKey: 'breadcrumbs.users', parent: '/' },
  { path: '/users/new', labelKey: 'breadcrumbs.newUser', parent: '/users' },
  {
    path: '/users/edit/:userId',
    labelKey: 'breadcrumbs.editUser',
    parent: '/users',
    dynamic: true,
  },
  { path: '/subscription', labelKey: 'breadcrumbs.subscription', parent: '/' },
  { path: '/plan-selection', labelKey: 'breadcrumbs.planSelection', parent: '/subscription' },
  { path: '/promotions', labelKey: 'breadcrumbs.promotions', parent: '/' },
  { path: '/settings', labelKey: 'breadcrumbs.settings', parent: '/' },
  { path: '/domains', labelKey: 'breadcrumbs.domains', parent: '/' },
  { path: '/fiscal-config', labelKey: 'breadcrumbs.fiscalConfig', parent: '/' },
  { path: '/partners', labelKey: 'breadcrumbs.partners', parent: '/' },
];

function findMatchingConfig(pathname) {
  // Exact match first
  const exact = ROUTE_CONFIG.find((r) => r.path === pathname);
  if (exact) return exact;

  // Dynamic match
  for (const route of ROUTE_CONFIG) {
    if (route.path.includes(':')) {
      const match = matchPath({ path: route.path, end: true }, pathname);
      if (match) return route;
    }
  }
  return null;
}

function buildTrail(pathname, t, dynamicLabel) {
  const config = findMatchingConfig(pathname);
  if (!config) return [];

  const trail = [];
  let current = config;

  while (current) {
    trail.unshift({
      path: current.path === config.path ? pathname : current.path,
      label:
        current.path === config.path && current.dynamic && dynamicLabel
          ? dynamicLabel
          : t(current.labelKey),
      isCurrent: current.path === config.path,
    });

    if (current.parent === null) break;
    // eslint-disable-next-line no-loop-func
    current = ROUTE_CONFIG.find((r) => r.path === current.parent);
  }

  return trail;
}

function Breadcrumbs() {
  const location = useLocation();
  const { t } = useTranslation();
  const { dynamicLabel } = useBreadcrumb();

  // Don't render on dashboard
  if (location.pathname === '/') return null;

  const trail = buildTrail(location.pathname, t, dynamicLabel);
  if (trail.length === 0) return null;

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <ol className="breadcrumbs-list">
        {trail.map((crumb, index) => (
          <li key={crumb.path} className="breadcrumbs-item">
            {index > 0 && (
              <span className="breadcrumbs-separator material-icons">chevron_right</span>
            )}
            {crumb.isCurrent ? (
              <span className="breadcrumbs-current">{crumb.label}</span>
            ) : (
              <Link to={crumb.path} className="breadcrumbs-link">
                {crumb.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default Breadcrumbs;
